import { InfoOutlined } from "@mui/icons-material";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Tooltip } from "../../../components";
import { useLocalization } from "../../../hooks/useLocalization";

interface RenewalViewProps {
  renewalDate?: Date;
  seats?: number;
  renewalPeriodDays?: number;
}

export const RenewalView: FC<RenewalViewProps> = ({
  renewalDate,
  seats,
  renewalPeriodDays,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  // Format the next renewal date as YYYY-MM-DD
  const nextDateFormatted = useMemo(() => {
    if (!renewalDate) {
      return null;
    }

    const year = renewalDate.getFullYear();
    const month = String(renewalDate.getMonth() + 1).padStart(2, "0");
    const day = String(renewalDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }, [renewalDate]);

  // Don't show renewal info for unlimited seats
  if (!seats) {
    return null;
  }

  // Don't show if no renewal date is set
  if (!renewalDate || !nextDateFormatted) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Stack spacing={0}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: 14,
            color: theme.colors.text[5],
          }}
        >
          {t("licenses.renewalDaysValue", { days: renewalPeriodDays })}
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: 12,
            color: theme.colors.text[1],
          }}
        >
          {t("licenses.nextRenewalDate", { date: nextDateFormatted })}
        </Typography>
      </Stack>
      <Tooltip message={t("licenses.renewalTooltip")} placement="right">
        <InfoOutlined
          sx={{
            fontSize: 18,
            color: theme.colors.secondary[6],
          }}
        />
      </Tooltip>
    </Box>
  );
};
